import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { notificationService } from "@/services/notificationService";

/** 서비스워커(sw.ts)가 푸시 클릭 시 이동 URL 에 붙이는 알림 ID 파라미터 이름 */
export const PUSH_NOTIFICATION_READ_PARAM = "notif";

/**
 * 푸시 알림 클릭으로 진입한 페이지에서 해당 알림을 읽음 처리한다.
 *
 * 서비스워커는 알림 클릭 시 `?notif=<알림ID>` 를 붙여 페이지를 연다(앱이 닫혀 있어
 * 메시지를 받을 탭이 없거나, 전체 재로드로 메시지가 유실되는 경우를 모두 커버).
 * 여기서 파라미터를 읽어 서버에 읽음 처리하고, URL 에서는 즉시 제거한다(replace).
 */
export function usePushNotificationRead(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handledRef = useRef<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get(PUSH_NOTIFICATION_READ_PARAM);
    if (raw === null) return;

    params.delete(PUSH_NOTIFICATION_READ_PARAM);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true },
    );

    const notificationId = Number(raw);
    if (!Number.isInteger(notificationId) || notificationId <= 0) return;
    if (handledRef.current === notificationId) return;
    handledRef.current = notificationId;

    notificationService
      .markAsRead(notificationId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      })
      .catch(() => {
        // 이미 읽음/삭제된 알림 등 — 비핵심 동작이므로 무시
      });
  }, [location.pathname, location.search, location.hash, navigate, queryClient]);
}
